import { useTranslation } from 'react-i18next';
import { BodyText, Card, withStyles, type PartnerOnboardingEntityType } from 'vopay';
import { Title, Button, CircleButton } from 'UI';
import type { OpenDialogProps } from '@/providers/Dialog.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useToast } from '@/providers/Toast.provider';
import useResendOnboardingEmail from '@/hooks/useApi/useResendOnboardingEmail';
import SendIcon from './Send.svg';
import styles from './SendEmailDialog.module.scss';

const cn = withStyles(styles);

/** UTC (`YYYY-MM-DD HH:MM:SS`): the new "Sent on" date `/resend` returns. */
export type SendEmailDialogResult = { sentDate: string };

type SendEmailDialogProps = OpenDialogProps<SendEmailDialogResult> & {
  email: string;
  entityType: PartnerOnboardingEntityType;
  entityID: string;
};

/** Confirms and sends the onboarding app's welcome email again, via `/partner/onboarding/application/resend`. */
export default function SendEmailDialog({ onConfirm, onCancel, email, entityType, entityID }: SendEmailDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.OnboardingEmail' });

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const toast = useToast();
  const { resendingOnboardingEmail, resendOnboardingEmail } = useResendOnboardingEmail();

  async function handleSend() {
    try {
      const res = await resendOnboardingEmail({ EntityType: entityType, EntityID: entityID });
      onConfirm({ sentDate: res.SentDate });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('sendError', { defaultValue: 'Could not send the onboarding email: {{errorMessage}}', errorMessage }),
      );

      // Same outcome as before the switch to /resend: a failed send closes the dialog and tells the user why.
      onCancel();
    }
  }

  return (
    <Card hoverEffect={false} className={cn('dialog')}>
      <div className={cn('closeButton')}>
        <CircleButton colorScheme="neutral" icon="close" size="large" onClick={onCancel} />
      </div>

      <img className={cn('image')} src={SendIcon} alt={t('sendIconAlt', { defaultValue: 'Send Email' })} />

      <div className={cn('header')}>
        <Title level="3">{t('confirmTitle', { defaultValue: 'Send Application Form' })}</Title>

        <BodyText className={cn('greyText')}>
          {t('confirmBody', {
            defaultValue: 'Are you sure you want to send the onboarding email to {{email}}?',
            email,
          })}
        </BodyText>
      </div>

      <div className={cn('buttons')}>
        <Button colorScheme="secondary" size="small" onClick={onCancel}>
          {t('cancelButton', { defaultValue: 'Cancel' })}
        </Button>

        {/* The old hook raised the fullscreen loader; a disabled button is enough to stop a double send
            (loading-conventions: block only when the flow demands it). */}
        <Button
          id="send-email-confirm"
          colorScheme="primary"
          size="small"
          disabled={isRoleViewOnly || resendingOnboardingEmail}
          onClick={handleSend}
        >
          {t('sendButton', { defaultValue: 'Send email' })}
        </Button>
      </div>
    </Card>
  );
}
